/**
 * Leading meal-time shorthand in free-text descriptions (e.g. "12:00 Scrambled eggs").
 * Parsed time is stored separately; food text must not include the prefix.
 */
const LEADING_TIME_RE = /^(\d{1,2}):(\d{2})\s*(?:(a\.?m\.?|p\.?m\.?))?\.?\s*(?:[-–—]\s*)?/i;
function to24HourTime(hour, minute, ampm) {
    if (minute < 0 || minute > 59) {
        return null;
    }
    const normalized = (ampm ?? '').toLowerCase().replace(/\./g, '');
    if (normalized === 'am' || normalized === 'a') {
        if (hour < 1 || hour > 12) {
            return null;
        }
        const h24 = hour === 12 ? 0 : hour;
        return `${String(h24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    }
    if (normalized === 'pm' || normalized === 'p') {
        if (hour < 1 || hour > 12) {
            return null;
        }
        const h24 = hour === 12 ? 12 : hour + 12;
        return `${String(h24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    }
    if (hour < 0 || hour > 23) {
        return null;
    }
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}
/** Normalize DB/API values like `12:00:00` → `12:00`. */
export function normalizeConsumedTimeValue(value) {
    if (value == null) {
        return null;
    }
    const trimmed = String(value).trim();
    if (!trimmed) {
        return null;
    }
    const match = trimmed.match(/^(\d{1,2}):(\d{2})/);
    if (!match) {
        return null;
    }
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
        return null;
    }
    return to24HourTime(hour, minute) ?? null;
}
/**
 * Parse an optional leading clock time from the start of a meal description.
 * Does not match `12 eggs` (no colon) or `2:1 ratio` (single digit after colon).
 */
export function parseLeadingMealTime(description) {
    const trimmed = description.trim();
    if (!trimmed) {
        return { consumedTime: null, foodText: '' };
    }
    const match = trimmed.match(LEADING_TIME_RE);
    if (!match) {
        return { consumedTime: null, foodText: trimmed };
    }
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    const ampm = match[3];
    const consumedTime = to24HourTime(hour, minute, ampm);
    if (!consumedTime) {
        return { consumedTime: null, foodText: trimmed };
    }
    const foodText = trimmed.slice(match[0].length).trim();
    return { consumedTime, foodText };
}
/**
 * Strip a leading time prefix and choose persisted consumed time.
 * Explicit picker/API `consumedTime` wins over parsed prefix when provided.
 */
export function normalizeMealDescriptionForPersistence(rawDescription, explicitConsumedTime) {
    const trimmed = rawDescription.trim().replace(/,\s*$/, '');
    const parsed = parseLeadingMealTime(trimmed);
    const explicit = normalizeConsumedTimeValue(explicitConsumedTime ?? null);
    return {
        description: parsed.foodText.trim(),
        consumedTime: explicit ?? parsed.consumedTime,
    };
}
export function formatMealTimeForDisplay(consumedTime, locale) {
    const normalized = normalizeConsumedTimeValue(consumedTime);
    if (!normalized) {
        return consumedTime;
    }
    const [hStr, mStr] = normalized.split(':');
    const hour = Number(hStr);
    const minute = Number(mStr);
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
        return consumedTime;
    }
    const date = new Date(2000, 0, 1, hour, minute, 0, 0);
    return date.toLocaleTimeString(locale, {
        hour: 'numeric',
        minute: '2-digit',
    });
}
/** Compact coach-grid label, e.g. `12:00p`, `7:30a`. */
export function formatMealTimeCompact(consumedTime) {
    const normalized = normalizeConsumedTimeValue(consumedTime);
    if (!normalized) {
        return consumedTime;
    }
    const [hStr, mStr] = normalized.split(':');
    let hour = Number(hStr);
    const minute = Number(mStr);
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
        return consumedTime;
    }
    const period = hour >= 12 ? 'p' : 'a';
    hour = hour % 12 || 12;
    return minute === 0
        ? `${hour}:00${period}`
        : `${hour}:${String(minute).padStart(2, '0')}${period}`;
}
